import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import PaymentsIcon from "@mui/icons-material/Payments";
import BasicDetails from "./components/forms/BasicDetails";
import PersonalDetails from "./components/forms/PersonalDetails";
import { useSelector } from "react-redux";
import EducationalDetails from "./components/forms/EducationalDetails";
import EmploymentDetails from "./components/forms/EmploymentDetails";
import CompensationBenefits from "./components/forms/CompensationBenefits";
import JoiningForms from "./components/forms/JoiningForms";
import BankDetails from "./components/forms/BankDetails";
import ProfileDetails from "./components/ProfileDetails";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { getEmployeeById } from "../../../../api/employeeApi";
import {
  setBasicDetails,
  resetBasicDetails,
  setBankDetails,
  setEducationList,
  setEmploymentList,
  setCompensation,
  setJoiningForms
} from "../../../../store/basicDetailsSlice";
import { useDispatch } from "react-redux";


const AddEmployee = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { tab, id } = useParams();
  const [loading, setLoading] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const handleRefetch = useCallback(() => {
    setRefetchTrigger(prev => prev + 1);
  }, []);

  // Validation helpers
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidPhone = (phone) => /^\d{10}$/.test(phone.replace(/\D/g, ''));

  // Optimized parameter mapping with validation and decoding
  const preFilledData = useMemo(() => {
    const paramMapping = {
      'firstName': 'firstName',
      'lastName': 'lastName',
      'email': 'personalEmail',
      'mobileNumber': 'personalPhone',
      'entityId': 'entityId',
      'departmentId': 'departmentId',
      'roleId': 'roleId',
      'designationId': 'designationId',
      'educationLevel': 'educationLevel',
      'jobLocation': 'jobLocation',
      'employeeId': 'employeeId',
      'dateOfJoining': 'dateOfJoining',
      'isFresher': 'isFresher',
      'hasExistingPF': 'hasExistingPF'
    };

    const decodedData = {};

    Object.entries(paramMapping).forEach(([urlParam, formField]) => {
      const value = searchParams.get(urlParam);
      if (value) {
        try {
          // Decode URL-encoded values and trim whitespace
          const decodedValue = decodeURIComponent(value).trim();
          // Additional validation for specific fields
          if (formField === 'personalEmail' && !isValidEmail(decodedValue)) {
            console.warn(`Invalid email format in URL parameter: ${urlParam}`);
            decodedData[formField] = '';
          } else if (formField === 'personalPhone' && !isValidPhone(decodedValue)) {
            console.warn(`Invalid phone format in URL parameter: ${urlParam}`);
            decodedData[formField] = '';
          } else {
            decodedData[formField] = decodedValue;
          }
        } catch (error) {
          console.error(`Error decoding URL parameter ${urlParam}:`, error);
          decodedData[formField] = '';
        }
      } else {
        decodedData[formField] = '';
      }
    });

    return decodedData;
  }, [searchParams]);


  const steps = useMemo(
    () => [
      {
        label: "Basic Details",
        value: "basic-details",
        icon: <PersonOutlineIcon fontSize="small" />,
      },
      {
        label: "Personal Information",
        value: "personal-information",
        icon: <PersonOutlineIcon fontSize="small" />,
      },
      {
        label: "Bank Details",
        value: "bank-details",
        icon: <AccountBalanceIcon fontSize="small" />,
      },
      {
        label: "Education",
        value: "education",
        icon: <SchoolOutlinedIcon fontSize="small" />
      },
      {
        label: "Previous Employment",
        value: "previous-employment",
        icon: <WorkOutlineIcon fontSize="small" />,
      },
      {
        label: "CTC Information",
        value: "compensation-benefits",
        icon: <PaymentsIcon fontSize="small" />,
      },
      {
        label: "Joining Forms",
        value: "joining-forms",
        icon: <PaymentsIcon fontSize="small" />,
      },
    ],
    [],
  );

  const [activeStep, setActiveStep] = useState(0);

  // Fetch employee details if ID is present
  useEffect(() => {
    const fetchEmployeeDetails = async () => {
      if (!id) {
        dispatch(resetBasicDetails());
        return;
      }

      try {
        setLoading(true);
        const response = await getEmployeeById(id);
        if (response.success) {
          const emp = response.data;

          // Find specific addresses
          const presentAddr = emp.addresses?.find(a => a.addressType === 'present') || {};
          const permanentAddr = emp.addresses?.find(a => a.addressType === 'permanent') || {};

          // Find identities - handle potential mapping differences and singular/plural
          const identityList = Array.isArray(emp.identities) ? emp.identities : (emp.identity ? [emp.identity] : []);
          const aadharIdentity = identityList.find(i => i.aadhaarNumber || i.aadharNumber || i.aadharNo) || {};
          const panIdentity = identityList.find(i => i.panNumber || i.panNo) || {};

          // Map backend data to frontend form state
          const mappedData = {
            firstName: emp.firstName || "",
            lastName: emp.lastName || "",
            mobileNumber: emp.phone || "",
            emailAddress: emp.email || "",
            entity: emp.entityId || emp.entity?.id || emp.entity?._id || "",
            jobLocation: emp.workLocation || emp.location?.id || emp.location?._id || "",
            department: emp.departmentId || emp.department?.id || emp.department?._id || "",
            systemRole: emp.roleId || emp.role?.id || emp.role?._id || "",
            designation: emp.designationId || emp.designation?.id || emp.designation?._id || "",
            dateOfJoining: (emp.dateOfJoining || emp.joiningDate) ? (emp.dateOfJoining || emp.joiningDate).split('T')[0] : "",
            employmentType: emp.employmentType || "Full-time",
            gender: emp.personalInfo?.gender || "",
            passportPhotoPath: emp.personalInfo?.passportPhotoPath || "",
            dob: emp.personalInfo?.dob || "",
            personalEmail: emp.personalInfo?.personalEmail || "",
            personalPhone: emp.personalInfo?.personalPhone || "",
            alternativePhone: emp.personalInfo?.alternativePhone || "",
            maritalStatus: emp.personalInfo?.maritalStatus || "",
            bloodGroup: emp.personalInfo?.bloodGroup || "",
            nationality: emp.personalInfo?.nationality || "Indian",
            religion: emp.personalInfo?.religion || "",
            fatherName: emp.personalInfo?.fatherName || "",
            motherName: emp.personalInfo?.motherName || "",
            aadharNo: aadharIdentity.aadhaarNumber || aadharIdentity.aadharNumber || aadharIdentity.aadharNo || "",
            aadharName: aadharIdentity.aadhaarName || aadharIdentity.aadharName || "",
            aadharFrontFiles: (() => {
              const doc = emp.documents?.find(d => d.documentType === 'Aadhar Front' || d.documentType === 'Aadhaar Front' || d.documentType === 'Aadhar Front Copy');
              return doc ? [{ name: doc.documentName, url: doc.fileUrl, isExisting: true }] : (aadharIdentity.aadhaarFrontFilePath ? [{ name: 'Aadhaar Front Copy', url: aadharIdentity.aadhaarFrontFilePath, isExisting: true }] : []);
            })(),
            aadharBackFiles: (() => {
              const doc = emp.documents?.find(d => d.documentType === 'Aadhar Back' || d.documentType === 'Aadhaar Back' || d.documentType === 'Aadhar Back Copy');
              return doc ? [{ name: doc.documentName, url: doc.fileUrl, isExisting: true }] : (aadharIdentity.aadhaarBackFilePath ? [{ name: 'Aadhaar Back Copy', url: aadharIdentity.aadhaarBackFilePath, isExisting: true }] : []);
            })(),
            panNo: panIdentity.panNumber || panIdentity.panNo || "",
            panName: panIdentity.panName || "",
            panFiles: (() => {
              const doc = emp.documents?.find(d => d.documentType === 'PAN' || d.documentType === 'PAN Copy' || d.documentType === 'PAN Card');
              return doc ? [{ name: doc.documentName, url: doc.fileUrl, isExisting: true }] : (panIdentity.panFilePath ? [{ name: 'PAN Copy', url: panIdentity.panFilePath, isExisting: true }] : []);
            })(),
            presentAddress: {
              accommodationType: presentAddr.accommodationType || "",
              houseNo: presentAddr.houseNo || "",
              floor: presentAddr.floorNo || "",
              buildingName: presentAddr.houseName || "",
              area: presentAddr.street || "",
              landmark: presentAddr.landmark || "",
              city: presentAddr.city || "",
              state: presentAddr.state || "",
              pincode: presentAddr.pincode || "",
            },
            permanentAddress: {
              houseNo: permanentAddr.houseNo || "",
              floor: permanentAddr.floorNo || "",
              buildingName: permanentAddr.houseName || "",
              area: permanentAddr.street || "",
              landmark: permanentAddr.landmark || "",
              city: permanentAddr.city || "",
              state: permanentAddr.state || "",
              pincode: permanentAddr.pincode || "",
            },
            physicallyChallenged: emp.personalInfo?.physicallyChallenged || (emp.personalInfo?.isPhysicallyChallenged ? "Yes" : "No"),
            physicallyChallengedDetails: emp.personalInfo?.physicallyChallengedDetails || "",
            emergencyName: emp.personalInfo?.emergencyContactName || "",
            emergencyRelation: emp.personalInfo?.emergencyContactRelation || "",
            emergencyPhone: emp.personalInfo?.emergencyContactPhone || "",
            spouseName: emp.personalInfo?.spouseName || "",
            sameAsPresent: permanentAddr.isSameAsPresent !== undefined ? permanentAddr.isSameAsPresent : (emp.addresses?.length === 1),
            employeeId: emp.employeeId || "",
            nominees: emp.nominees || [],
            disabilityCertificateFiles: (() => {
              const doc = emp.documents?.find(d => d.documentType === 'Disability Certificate' || d.documentType === 'Disability Certificate Copy' || d.documentType === 'Physically Challenged Certificate');
              return doc ? [{ name: doc.documentName, url: doc.fileUrl, isExisting: true }] : (emp.personalInfo?.physicallyChallengedCertificatePath ? [{ name: 'Disability Certificate', url: emp.personalInfo.physicallyChallengedCertificatePath, isExisting: true }] : []);
            })(),
          };

          dispatch(setBasicDetails(mappedData));

          // Map bank details
          if (emp.bankDetails) {
            const bank = emp.bankDetails;
            dispatch(setBankDetails({
              accountName: bank.accountName || "",
              accountNumber: bank.accountNumber || "",
              bankName: bank.bankName || "",
              ifscCode: bank.ifscCode || "",
              branchName: bank.branchName || "",
            }));
          }

          // Map education list
          if (emp.education && emp.education.length > 0) {
            dispatch(setEducationList(emp.education.map(edu => ({
              qualificationLevel: edu.qualificationLevel || "",
              degree: edu.degree || "",
              yearOfJoining: edu.yearOfJoining || "",
              yearOfCompletion: edu.yearOfCompletion || "",
              collegeName: edu.collegeName || "",
              university: edu.university || edu.universityName || "",
              cgpa: edu.cgpaPercentage || edu.cgpa || "",
              state: edu.state || "",
              attachments: edu.documentPath ? [{ name: 'Existing Document', url: edu.documentPath, isExisting: true }] : []
            }))));
          }

          // Map employment list
          if (emp.experience && emp.experience.length > 0) {
            dispatch(setEmploymentList(emp.experience.map(exp => ({
              companyName: exp.companyName || "",
              designation: exp.designation || "",
              ctc: exp.ctc || "",
              joiningDate: exp.dateOfJoining || exp.fromDate || "",
              relievingDate: exp.dateOfRelieving || exp.toDate || "",
              address: exp.address || "",
              hrName: exp.hrName || "",
              hrPhone: exp.hrPhone || "",
              hrEmail: exp.hrEmail || "",
              managerName: exp.managerName || "",
              managerPhone: exp.managerPhone || exp.reportingManagerPhone || "",
              references: exp.references || [{ name: exp.referenceName || "", phone: exp.referencePhone || "", email: exp.referenceEmail || "" }],
              offerLetter: exp.offerLetter || "",
              incrementLetter: exp.incrementLetter || "",
              payslips: exp.payslips || "",
              experienceLetter: exp.experienceLetter || "",
              relievingLetter: exp.relievingLetter || "",
              offerLetterFiles: exp.offerLetter ? [exp.offerLetter] : [],
              incrementLetterFiles: exp.incrementLetter ? [exp.incrementLetter] : [],
              payslipFiles: exp.payslips ? [exp.payslips] : [],
              experienceLetterFiles: exp.experienceLetter ? [exp.experienceLetter] : [],
              relievingLetterFiles: exp.relievingLetter ? [exp.relievingLetter] : [],
              isLatest: exp.isLatest || false
            }))));
          }
          // Map compensation details
          if (emp.compensation) {
            const comp = emp.compensation;
            const nonPfDoc = emp.documents?.find(d => d.documentType === 'Declaration of Non-PF' || d.documentType === 'Joining Form: Declaration of Non-PF');
            dispatch(setCompensation({
              ctc: comp.ctc || "",
              gross: comp.monthlyGross || "",
              hasExistingPF: comp.hasExistingPF ? "yes" : "no",
              uanNumber: comp.uanNumber || "",
              esicNumber: comp.esicNumber || "",
              flexiBenefits: comp.flexiBenefits || "",
              tds: comp.tds || "",
              needFillDetails: comp.needFillDetails || "",
              healthCheckDoc: comp.healthCheckDoc ? [{ name: 'Existing Document', url: comp.healthCheckDoc, isExisting: true }] : null,
              declarationOfNonPfDoc: nonPfDoc ? [{ name: nonPfDoc.documentName, url: nonPfDoc.fileUrl, isExisting: true }] : null,
              declaration: comp.declarationAgreed || false,
            }));
          }

          // Map joining forms dynamically
          if (emp.documents && emp.documents.length > 0) {
            const joiningFormsData = {};
            const standardFields = [
              'applicationFormITS',
              'esicForm',
              'form11',
              'itsFormFGratuity',
              'itsHRPOSHAcknowledgement',
              'pfForm2',
              'policeVerification',
              'passportPhoto',
              'OfferLetterAcceptance',
              'AppointmentLetterAcknowledgement'
            ];

            emp.documents.forEach(doc => {
              if (doc.documentType.startsWith('Joining Form: ')) {
                const field = doc.documentType.replace('Joining Form: ', '');
                joiningFormsData[field] = [{ name: doc.documentName, url: doc.fileUrl, isExisting: true }];
              } else if (standardFields.includes(doc.documentType)) {
                joiningFormsData[doc.documentType] = [{ name: doc.documentName, url: doc.fileUrl, isExisting: true }];
              }
            });
            dispatch(setJoiningForms(joiningFormsData));
          }
        }
      } catch (error) {
        console.error("Error fetching employee details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeDetails();
  }, [id, dispatch, refetchTrigger]);

  // Handle tab navigation based on URL parameter
  useEffect(() => {
    if (tab) {
      const stepIndex = steps.findIndex(step => step.value === tab);
      if (stepIndex !== -1) {
        setActiveStep(stepIndex);
      }
    } else {
      // Navigate to default tab if no tab parameter
      navigate(`/my-team/add-employee/${steps[0].value}`, { replace: true });
    }
  }, [tab, steps, navigate]);

  // Use basicDetails state consistently
  const formData = useSelector((state) => state.basicDetails.formData) || {};



  // Memoized callbacks to prevent unnecessary re-renders
  const handleBasicDetailsNext = useCallback((employeeId) => {
    if (steps[1]) {
      if (employeeId) {
        navigate(`/my-team/add-employee/${employeeId}/${steps[1].value}`);
      } else {
        navigate(`/my-team/add-employee/${steps[1].value}`);
      }
    }
  }, [steps, navigate]);

  const handlePersonalDetailsNext = useCallback(() => {
    if (steps[2]) {
      if (id) {
        navigate(`/my-team/add-employee/${id}/${steps[2].value}`);
      } else {
        navigate(`/my-team/add-employee/${steps[2].value}`);
      }
    }
  }, [steps, navigate, id]);

  const handleBankDetailsNext = useCallback(() => {
    if (steps[3]) {
      if (id) {
        navigate(`/my-team/add-employee/${id}/${steps[3].value}`);
      } else {
        navigate(`/my-team/add-employee/${steps[3].value}`);
      }
    }
  }, [steps, navigate, id]);

  const handleBankDetailsBack = useCallback(() => {
    if (steps[1]) {
      if (id) {
        navigate(`/my-team/add-employee/${id}/${steps[1].value}`);
      } else {
        navigate(`/my-team/add-employee/${steps[1].value}`);
      }
    }
  }, [steps, navigate, id]);


  const handleEducationNext = useCallback(() => {
    if (steps[4]) {
      if (id) {
        navigate(`/my-team/add-employee/${id}/${steps[4].value}`);
      } else {
        navigate(`/my-team/add-employee/${steps[4].value}`);
      }
    }
  }, [steps, navigate, id]);

  const handleEducationBack = useCallback(() => {
    if (id) {
      navigate(`/my-team/add-employee/${id}/${steps[2].value}`);
    } else {
      navigate(`/my-team/add-employee/${steps[2].value}`);
    }
  }, [steps, navigate, id]);


  const handleEmploymentNext = useCallback(() => {
    if (steps[5]) {
      if (id) {
        navigate(`/my-team/add-employee/${id}/${steps[5].value}`);
      } else {
        navigate(`/my-team/add-employee/${steps[5].value}`);
      }
    }
  }, [steps, navigate, id]);

  const handleEmploymentBack = useCallback(() => {
    if (steps[3]) {
      if (id) {
        navigate(`/my-team/add-employee/${id}/${steps[3].value}`);
      } else {
        navigate(`/my-team/add-employee/${steps[3].value}`);
      }
    }
  }, [steps, navigate, id]);


  const handleCompensationNext = useCallback(() => {
    if (steps[6]) {
      if (id) {
        navigate(`/my-team/add-employee/${id}/${steps[6].value}`);
      } else {
        navigate(`/my-team/add-employee/${steps[6].value}`);
      }
    }
  }, [steps, navigate, id]);

  const handleCompensationBack = useCallback(() => {
    if (steps[4]) {
      if (id) {
        navigate(`/my-team/add-employee/${id}/${steps[4].value}`);
      } else {
        navigate(`/my-team/add-employee/${steps[4].value}`);
      }
    }
  }, [steps, navigate, id]);

  const handleJoiningFormsNext = useCallback(() => {
    navigate('/my-team/employee-directory');
  }, [navigate]);

  const handleJoiningFormsBack = useCallback(() => {
    if (steps[5]) {
      if (id) {
        navigate(`/my-team/add-employee/${id}/${steps[5].value}`);
      } else {
        navigate(`/my-team/add-employee/${steps[5].value}`);
      }
    }
  }, [steps, navigate, id]);

  const renderStepContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (activeStep === 0) {
      return <BasicDetails preFilledData={preFilledData} onNext={handleBasicDetailsNext} employeeId={id} />;
    }

    if (activeStep === 1) {
      return <PersonalDetails preFilledData={preFilledData} onNext={handlePersonalDetailsNext} employeeId={id} />;
    }

    if (activeStep === 2) {
      return (
        <BankDetails
          onNext={handleBankDetailsNext}
          onBack={handleBankDetailsBack}
          employeeId={id}
        />
      );
    }

    if (activeStep === 3) {
      return (
        <EducationalDetails
          onNext={handleEducationNext}
          onBack={handleEducationBack}
          employeeId={id}
        />
      );
    }

    if (activeStep === 4) {
      return (
        <EmploymentDetails
          onNext={handleEmploymentNext}
          onBack={handleEmploymentBack}
          employeeId={id}
          onRefetch={handleRefetch}
        />
      );
    }

    if (activeStep === 5) {
      return (
        <CompensationBenefits
          onNext={handleCompensationNext}
          onBack={handleCompensationBack}
          employeeId={id}
        />
      );
    }

    if (activeStep === 6) {
      return (
        <JoiningForms
          onNext={handleJoiningFormsNext}
          onBack={handleJoiningFormsBack}
          employeeId={id}
        />
      );
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: { xs: 'column', md: 'row' }, minHeight: "calc(100vh - 64px)" }}>
      <Box sx={{ position: { xs: "static", md: "sticky" }, top: 0, height: { xs: "auto", md: "calc(100vh - 64px)" }, width: { xs: '100%', md: 'auto' }, overflow: "hidden", zIndex: 11 }}>
        <ProfileDetails
          activeStep={activeStep}
          setActiveStep={(index) => {
            if (index !== undefined && steps[index]) {
              if (id) {
                navigate(`/my-team/add-employee/${id}/${steps[index].value}`);
              } else {
                navigate(`/my-team/add-employee/${steps[index].value}`);
              }
            }
          }}
          employeeId={id}
        />
      </Box>

      <Box sx={{
        flex: 1,
        backgroundColor: "#FAF9FB",
        overflowY: "auto",
        maxHeight: { xs: 'none', md: "calc(100vh - 64px)" },
        scrollbarWidth: "none",
        width: '100%'
      }}>
        <Box sx={{
          px: 4,
          py: 2.25,
          borderBottom: "1px solid rgba(117, 34, 209, 0.08)",
          position: "sticky",
          top: 0,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          backdropFilter: "blur(12px)",
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0F0A1E", letterSpacing: '-0.01em' }}>
            {steps[activeStep]?.label}
          </Typography>
          {id && (
            <Box sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: '20px',
              bgcolor: 'rgba(117, 34, 209, 0.08)',
              border: '1px solid rgba(117, 34, 209, 0.12)',
            }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#7522D1' }}>
                Editing ID: {id}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ px: { xs: 2, md: 4 }, py: 4, pb: 12 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: '16px',
              border: '1px solid rgba(117, 34, 209, 0.08)',
              boxShadow: '0 8px 32px rgba(117, 34, 209, 0.03)',
              backgroundColor: '#FFFFFF',
            }}
          >
            {renderStepContent()}
          </Paper>
        </Box>
      </Box>

    </Box>
  );
};

export default AddEmployee;
